<template>
    <div>
        <v-container>
            <v-form ref="invoiceForm" v-model="valid" lazy-validation>
                <v-row justify="space-around">
                    <v-col cols="12" md="5">
                        {{ $t('customer') }}: {{ newInvoice.customer.name }}
                        <br />
                        <br />
                        <v-text-field
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                            label="JBKJS" v-model="newInvoice.customer.jbkjs">
                        </v-text-field>
                        <v-text-field
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                            v-bind:label="$t('address')" v-model="newInvoice.customer.street"
                            :rules="[v => !!v || $t('required')]">
                        </v-text-field>
                        <v-text-field
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                            v-bind:label="$t('postalZone')" v-model="newInvoice.customer.postalZone"
                            :rules="[v => !!v || $t('required')]">
                        </v-text-field>
                        <v-text-field
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                            v-bind:label="$t('city')" v-model="newInvoice.customer.city"
                            :rules="[v => !!v || $t('required')]">
                        </v-text-field>
                    </v-col>
                    <v-col cols="12" md="5">
                        {{ $t('status') }}: {{ documentStatusText() }}
                        <br />
                        <span v-if="newInvoice.rejectionNote.length > 0">{{ $t('reason') }}: {{ newInvoice.rejectionNote
                            }} </span>
                    </v-col>
                </v-row>
                <v-row>
                    <v-divider class="mt-5"></v-divider>
                </v-row>
                <v-row justify="space-around">
                    <v-col cols="12" md="5">
                        <v-select attach v-model="newInvoice.currencyCode" :items="currencies" item-text="currencyCode"
                            item-value="currencyCode"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            v-bind:label="$t('currency')" @change="currencyChange" v-bind:no-data-text="$t('noData')">
                            <template slot="item" slot-scope="{ item }">
                                <v-tooltip bottom color="#2b5d66">
                                    <template #activator="{ on, attrs }">
                                        <v-layout wrap v-on="on" v-bind="attrs">
                                            <v-list-item-content>
                                                <v-list-item-title>{{ item.currencyCode }}</v-list-item-title>
                                            </v-list-item-content>
                                        </v-layout>
                                    </template>
                                    <span>{{ item.description }}</span>
                                </v-tooltip>
                            </template>
                        </v-select>
                        <v-text-field v-if="newInvoice.currencyCode != 'RSD'" v-model="newInvoice.exchangeRate"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            :rules="[v => !!v || '!', v => (/^(?!,$)[\d,.]+$/.test(v) && parseFloat(convertFormattedStringToNumber(v)) > 0) || $t('number') + ' > 0']"
                            v-bind:label="$t('exchangeRate') + '*'" @change="formatValue()" @input="calculateTotal()"
                            required>
                        </v-text-field>
                        <v-textarea auto-grow rows="1" v-model.trim="newInvoice.invoice" ref="invoiceNumberRef"
                            v-bind:label="getInvoiceNumberLabel()"
                            :disabled="!(newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22 || (newInvoice.statusId == 19 && validationPermission()))"
                            :rules="invoiceNumberRule()" @input="checkInvoiceNumber()" required>
                        </v-textarea>
                        <v-textarea auto-grow rows="1" v-model.trim="newInvoice.contractDocumentReference"
                            v-bind:label="getContractLabel()"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            :rules="contractRule()" required>
                        </v-textarea>
                        <v-select attach
                            v-if="newInvoice.documentType.documentTypeId == 2 || newInvoice.documentType.documentTypeId == 3 || newInvoice.documentType.documentTypeId == 6 || newInvoice.documentType.documentTypeId == 7"
                            v-model="newInvoice.billingReferenceTypeId" :items="billingReferenceTypeItems"
                            :item-text="getBillingReferenceTypeText" item-value="id"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            v-bind:label="getBillingReferenceLabel()" @change="changeBillingReferenceType"
                            v-bind:no-data-text="$t('noData')">
                        </v-select>
                        <v-combobox v-if="newInvoice.billingReferenceTypeId == 1" v-model="billingReference"
                            :items="billingReferencesItems" item-text="invoice" item-value="id"
                            :search-input.sync="search" @change="addBillingReference"
                            @click:clear="clearBillingReference" v-bind:label="$t('sourceInvoice')" append-icon=""
                            clearable
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            @input.native="billingReference = $event.srcElement.value">
                            <v-tooltip right slot="append-outer" color="#2b5d66">
                                <template #activator="{ on }">
                                    <v-icon v-on="on"
                                        v-show="billingReferencesItems.length == 0 && newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                        @click="addNewBillingReference" color="#2b5d66">control_point</v-icon>
                                </template>
                                <span>{{ $t('addSourceInvoice') }}</span>
                            </v-tooltip>
                        </v-combobox>
                        <v-combobox v-if="newInvoice.billingReferenceTypeId == 3" v-model="billingReference"
                            :items="billingReferencesItems" item-text="invoice" item-value="id"
                            :search-input.sync="searchPrepaymentBillingReference" @change="addBillingReference"
                            @click:clear="clearBillingReference" v-bind:label="$t('sourceInvoice')" append-icon=""
                            clearable
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            @input.native="billingReference = $event.srcElement.value">
                            <v-tooltip right slot="append-outer" color="#2b5d66">
                                <template #activator="{ on }">
                                    <v-icon v-on="on"
                                        v-show="billingReferencesItems.length == 0 && newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                        @click="addNewBillingReference" color="#2b5d66">control_point</v-icon>
                                </template>
                                <span>{{ $t('addSourceInvoice') }}</span>
                            </v-tooltip>
                        </v-combobox>
                        <v-chip-group column>
                            <v-chip v-for="(reference, index) in newInvoice.billingReferences" :key="index"
                                :close="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                outlined @click="openBillingReference(reference)"
                                @click:close="removeFromBillingReferences(index)">
                                {{ reference.invoice }}
                            </v-chip>
                        </v-chip-group>
                        <v-menu v-if="newInvoice.billingReferenceTypeId == 2" v-model="menuStart"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model.trim="newInvoice.startDate" v-bind:label="$t('periodOf') + '*'"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    prepend-inner-icon="mdi-calendar" readonly clearable v-bind="attrs" v-on="on"
                                    :rules="invoicePeriodStartRule()"></v-text-field>
                            </template>
                            <v-date-picker v-model="newInvoice.startDate" no-title scrollable min="2017-01-01"
                                max="2050-01-01" @input="menuStart = false">
                            </v-date-picker>
                        </v-menu>
                        <v-menu v-if="newInvoice.billingReferenceTypeId == 2" v-model="menuEnd"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-textarea auto-grow rows="1" v-model.trim="newInvoice.endDate"
                                    v-bind:label="$t('periodTo') + '*'"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    prepend-inner-icon="mdi-calendar" readonly clearable v-bind="attrs" v-on="on"
                                    :rules="invoicePeriodEndRule()"></v-textarea>
                            </template>
                            <v-date-picker v-model="newInvoice.endDate" no-title scrollable min="2017-01-01"
                                max="2050-01-01" @input="menuEnd = false">
                            </v-date-picker>
                        </v-menu>
                        <!--<select class="form-group col-sm-8" data-toggle="tooltip" data-placement="top" data-html="true">
            <option v-for="item in examptionReasonEItems" v-bind:key="item.key" :title="item.text">{{item.key}}</option>
        </select>-->
                        <span
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22 || newInvoice.taxExemptionReasons.length > 0">{{
                            $t('taxCategories') }}</span>
                        <table
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            v-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22 || newInvoice.taxExemptionReasons.length > 0"
                            style="border-spacing: 20px;border: 1px solid lightgray;padding:5px;">
                            <tbody>
                                <tr v-for="(reason, k) in newInvoice.taxExemptionReasons" :key="k">
                                    <td style="border: 1px solid lightgray;padding:15px;">
                                        <v-tooltip right color="#2b5d66">
                                            <template #activator="{ on, attrs }">
                                                <v-icon small v-bind="attrs" v-on="on"
                                                    v-show="newInvoice.taxExemptionReasons.length > 0 && (newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22)"
                                                    @click="deleteTaxExemptionReason(k)">mdi-delete</v-icon>
                                            </template>
                                            <span>{{ $t('deleteTaxCategory') }}</span>
                                        </v-tooltip>
                                        <v-select attach v-model="reason.category" :items="examptionRessonCategoryItems"
                                            item-text="category" item-value="category" v-bind:label="$t('vatCategory')"
                                            :rules="[newInvoice.taxExemptionReasons.length > 0 && categoryExists(reason) < 2 || $t('duplicatedTaxCategory'),
                                            newInvoice.taxExemptionReasons.length > 0 && reason.category.length > 0 || $t('vatCategoryRequired'),
                                            findItemCategory(reason.category) || $t('vatCategoryNotUsed')]"
                                            @change="clearTaxExemptionReasonKey(k); calculateTotal()"
                                            v-bind:no-data-text="$t('noData')"
                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            <template slot="item" slot-scope="{ item }">
                                                <v-tooltip bottom color="#2b5d66">
                                                    <template #activator="{ on, attrs }">
                                                        <v-layout wrap v-on="on" v-bind="attrs">
                                                            <v-list-item-content>
                                                                <v-list-item-title>{{ item.category
                                                                    }}</v-list-item-title>
                                                            </v-list-item-content>
                                                        </v-layout>
                                                    </template>
                                                    <span>{{ getDescriptionText(item) }}</span>
                                                </v-tooltip>
                                            </template>
                                        </v-select>
                                        <br />
                                        <v-select attach ref="selectTaxReason" v-model="reason.key"
                                            :items="getTaxExemptionReasonForCategory(reason.category)" item-text="key"
                                            item-value="key" v-bind:label="$t('reasonKey')"
                                            :rules="[newInvoice.taxExemptionReasons && newInvoice.taxExemptionReasons.length > 0 && reason.key && reason.key.length > 0 || $t('reasonKeyRequired')]"
                                            menu-props="auto" v-bind:no-data-text="$t('noData')"
                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            <template slot="item" slot-scope="{ item }">
                                                <v-tooltip bottom color="#2b5d66">
                                                    <template #activator="{ on, attrs }">
                                                        <v-layout wrap v-on="on" v-bind="attrs">
                                                            <v-list-item-content>
                                                                <v-list-item-title>{{ item.key }}</v-list-item-title>
                                                            </v-list-item-content>
                                                        </v-layout>
                                                    </template>
                                                    <span>{{ getReasonKeyText(item) }}</span>
                                                </v-tooltip>
                                            </template>
                                        </v-select>
                                        <br />
                                        <v-textarea auto-grow rows="1" v-model.trim="reason.taxExemptionReasonNumber"
                                            v-bind:label="$t('decisionNumber')"
                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                            clearable>
                                        </v-textarea>
                                    </td>
                                </tr>
                                <tr>
                                    <!-- Original -->
                                    <td style="height:30px;vertical-align:bottom;">
                                        <v-icon
                                            v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                            @click="addTax" color="#008B8B">control_point</v-icon> <span
                                            v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                            @click="addTax" style="cursor:pointer;color:#008B8B;">{{
                                            $t('addVatCategory') }}</span>
                                    </td>
                                    <!-- UC
                    <td style="height:30px;vertical-align:bottom;">
                        <v-icon
                        v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                        @click="addTax" color="#2b5d66">control_point</v-icon> <span v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22" @click="addTax" style="cursor:pointer;color:#2b5d66;">{{ $t('addVatCategory') }}</span>
                    </td> -->
                                </tr>
                            </tbody>
                        </table>

                        <!-- -->
                        <v-menu
                            v-if="newInvoice.documentType.documentTypeId == 1 || newInvoice.documentType.documentTypeId == 5"
                            v-model="menuDelivery" :close-on-content-click="false" transition="scale-transition"
                            offset-y min-width="auto"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model="newInvoice.deliveryDate" v-bind:label="$t('deliveryDate') + '*'"
                                    prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"></v-text-field>
                            </template>
                            <v-date-picker v-model="newInvoice.deliveryDate" no-title scrollable min="2017-01-01"
                                max="2050-01-01" @input="menuDelivery = false">
                            </v-date-picker>
                        </v-menu>
                        <v-menu
                            v-if="newInvoice.documentType.documentTypeId != 2 && newInvoice.documentType.documentTypeId != 6"
                            v-model="menuDue" :close-on-content-click="false" transition="scale-transition" offset-y
                            min-width="auto"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model="newInvoice.dueDate" v-bind:label="getDueDateLabel()"
                                    prepend-inner-icon="mdi-calendar" readonly v-bind="attrs" v-on="on"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"></v-text-field>
                            </template>
                            <v-date-picker v-model="newInvoice.dueDate" no-title scrollable min="2017-01-01"
                                max="2050-01-01" @input="menuDue = false">
                            </v-date-picker>
                        </v-menu>
                        <v-select attach
                            v-if="newInvoice.documentType.documentTypeId != 2 && newInvoice.documentType.documentTypeId != 6"
                            v-model="newInvoice.vatDate" :items="vatDateItems" :item-text="getDescriptionText"
                            item-value="code" v-bind:label="$t('vatDate')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            v-bind:no-data-text="$t('noData')" @change="changeVatDate" return-object>
                        </v-select>
                        <v-menu
                            v-if="(newInvoice.documentType.documentTypeId == 3 || newInvoice.documentType.documentTypeId == 7) && newInvoice.vatDate.code == 35"
                            v-model="menuContract"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto">
                            <template v-slot:activator="{ on, attrs }">
                                <v-text-field v-model.trim="newInvoice.contractDate"
                                    v-bind:label="$t('contractDate') + '*'"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    prepend-inner-icon="mdi-calendar" readonly clearable v-bind="attrs" v-on="on"
                                    :rules="invoiceContractDateRule()"></v-text-field>
                            </template>
                            <v-date-picker v-model="newInvoice.contractDate" no-title scrollable min="2017-01-01"
                                max="2050-01-01" @input="menuContract = false">
                            </v-date-picker>
                        </v-menu>
                    </v-col>
                    <v-col cols="12" md="5">
                        <v-textarea auto-grow rows="1" v-model.trim="newInvoice.referenceNumber"
                            v-bind:label="$t('referenceNumber')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-textarea>
                        <v-textarea auto-grow rows="1" v-model.trim="newInvoice.modelNumber"
                            v-bind:label="$t('modelNumber')" :rules="modelNumberRule()"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-textarea>
                        <v-textarea auto-grow rows="1" v-model.trim="newInvoice.purchaseOrder"
                            v-bind:label="$t('purchaseOrder')" :rules="purchaseOrderRule()"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-textarea>
                        <v-text-field v-model.trim="newInvoice.buyerReference" v-bind:label="$t('buyerReference')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-text-field>
                        <v-text-field v-model.trim="newInvoice.objectReference" v-bind:label="$t('objectReference')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-text-field>
                        <v-text-field v-model.trim="newInvoice.originatorDocumentReference"
                            v-bind:label="$t('originatorDocumentReference')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                        </v-text-field>
                        <v-text-field v-model.trim="despatchReference" v-bind:label="$t('despatchReference')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                            <v-tooltip right slot="append-outer" color="#2b5d66">
                                <template #activator="{ on }">
                                    <v-icon v-on="on"
                                        v-show="despatchReferencesItems.length == 0 && newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                        @click="addDespatchReference" color="#2b5d66">control_point</v-icon>
                                </template>
                                <span>{{ $t('addDespatch') }}</span>
                            </v-tooltip>
                        </v-text-field>
                        <v-chip-group column>
                            <v-chip v-for="(reference, index) in newInvoice.despatchReferences" :key="index"
                                :close="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                outlined @click:close="removeFromDespatchReferences(index)">
                                {{ reference.despatchReference }}
                            </v-chip>
                        </v-chip-group>
                        <v-textarea style="font-weight:bold" v-model.trim="newInvoice.note" v-bind:label="$t('note')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            auto-grow rows="1">
                        </v-textarea>
                        <v-checkbox v-if="getCustomerJbkjs() != null && getCustomerJbkjs().length > 0"
                            v-model="newInvoice.cir" v-bind:label="$t('sendToCir')"
                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                            color="#2b5d66">
                        </v-checkbox>
                        <table>
                            <tr>
                                <td style="width:7%">
                                    <v-tooltip right color="#2b5d66">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-badge bordered color="#2b5d66" overlap :content="fileCount()"
                                                :value="fileCount()">
                                                <v-icon v-bind="attrs" v-on="on"
                                                    @click="showAttachment = !showAttachment">
                                                    attach_file
                                                </v-icon>
                                            </v-badge>
                                        </template>
                                        <span>{{ $t('addAttachment') }}</span>
                                    </v-tooltip>
                                </td>
                                <td style="width:7%"><br /></td>
                                <td style="width:86%">
                                    <v-tooltip right color="#2b5d66">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon v-bind="attrs" v-on="on" @click="showOperation = !showOperation">
                                                assignment_ind
                                            </v-icon>
                                        </template>
                                        <span>{{ $t('history') }}</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                        </table>
                    </v-col>
                </v-row>
                <v-divider class="mb-10 mt-5"></v-divider>
                <table>
                    <thead>
                        <tr style="text-align:left;font-size:14px">
                            <th style="width:7%" v-if="newInvoice.documentType.documentTypeId < 5">{{ $t('code') }}</th>
                            <th style="width:7%" v-if="newInvoice.documentType.documentTypeId >= 5">{{ $t('code') }}*
                            </th>
                            <th style="width:20%">{{ $t('itemName') }}*</th>
                            <th style="width:7%">{{ $t('quantity') }}*</th>
                            <th style="width:7%">{{ $t('unitMeasure') }}*</th>
                            <th style="width:10%">{{ $t('price') }}*</th>
                            <th style="width:5%">{{ $t('discount') }} %</th>
                            <th style="width:5%">{{ $t('discountAmount') }}</th>
                            <th style="width:10%">{{ $t('amountWithoutVat') }}</th>
                            <th style="width:4%">{{ $t('vat') }}</th>
                            <th style="width:4%">{{ $t('vatCat') }}.</th>
                            <th v-if="findItemCategory('S20') || findItemCategory('S10')" style="width:8%">{{
                                $t('classification') }}
                            </th>
                            <th style="width:10.5%">{{ $t('amountWithVat') }}</th>
                            <th style="width:2.5%"></th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(item, k) in newInvoice.items" :key="k">
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense style="font-size:14px;"
                                                v-if="newInvoice.documentType.documentTypeId < 5 || (newInvoice.documentType.documentTypeId >= 5 && newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22)"
                                                v-model.trim="item.code"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                                @keyup.enter="addNewRow">
                                            </v-text-field>
                                            <v-select attach dense style="font-size:14px"
                                                v-if="newInvoice.documentType.documentTypeId >= 5 && (newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22)"
                                                v-model="item.code" :items="itemsCodes" item-text="code"
                                                item-value="code" :rules="[v => !!v || '!']"
                                                v-bind:no-data-text="$t('noData')"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            </v-select>
                                        </div>
                                    </template>
                                    <span>{{ item.code }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense required style="font-size:14px"
                                                :rules="[v => !!v || '!']" v-model.trim="item.name"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                                @keyup.enter="addNewRow">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.name }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense required style="font-size:14px"
                                                v-model.trim="item.quantity"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                                @change="addDecimals(item)"
                                                :rules="[v => !!v || '!', v => (item.taxCategory != 'N' && (/^(?!,$)[\d,.]+$/.test(v) && parseFloat(convertFormattedStringToNumber(v)) > 0)) || (item.taxCategory === 'N' && /^-(?!,$)[\d,.]+$/.test(v) && parseFloat(convertFormattedStringToNumber(v)) == -1) || getRuleText(item.taxCategory)]"
                                                @input="calculateLineTotal(item)" @keyup.enter="addNewRow">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.quantity }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-select attach dense style="font-size:14px" v-model="item.unitMeasure"
                                    :items="unitMeasureItems" :item-text="getNameText" item-value="code"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    v-bind:no-data-text="$t('noData')">
                                </v-select>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense required style="font-size:14px"
                                                :rules="[v => !!v || '!', v => (/^(?!,$)[\d,.]+$/.test(v) && parseFloat(convertFormattedStringToNumber(v)) > 0) || $t('number') + ' > 0']"
                                                v-model.trim="item.price" @input="calculateLineTotal(item)"
                                                @change="addDecimals(item)" @keyup.enter="addNewRow"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.price }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense style="font-size:14px"
                                                v-model.trim="item.discount"
                                                :rules="[v => /^\d*\,?\d*$/.test(v) || $t('number') + ' > 0']"
                                                v-on:blur="setDefaultValue(k)" @change="addDecimals(item)"
                                                @input="calculateLineTotal(item)" @keydown="clearDiscountAmount(item)"
                                                @keydown.enter="addNewRow"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.discount }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense style="font-size:14px"
                                                v-model.trim="item.discountAmount" v-on:blur="setDefaultValue(k)"
                                                :rules="[v => !!v || '!', v => (/^(?!,$)[\d,.]+$/.test(v) && parseFloat(convertFormattedStringToNumber(v)) >= 0) || $t('number') + ' > 0']"
                                                @keyup.enter="addNewRow" @change="addDecimals(item)"
                                                @keydown="clearDiscount(k)" @input="calculateLineTotal(item)"
                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.discountAmount }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field dense style="font-size:14px" readonly
                                                v-model="item.amountWithoutVat">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.amountWithoutVat }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field filled dense style="font-size:14px" v-model.trim="item.vat"
                                                disabled>
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.vat }}</span>
                                </v-tooltip>
                            </td>
                            <td>
                                <v-select dense style="font-size:14px" v-model="item.taxCategory"
                                    :items="taxCategoryItems" item-text="category" item-value="category"
                                    v-bind:no-data-text="$t('noData')"
                                    :rules="[v => (item.taxCategory == 'S20' || item.taxCategory == 'S10' || (newInvoice.taxExemptionReasons.length > 0 && findCategory(item.taxCategory))) || '!']"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    @change="changeTaxCategory(item)">
                                    <template slot="item" slot-scope="{ item }">
                                        <v-tooltip bottom color="#2b5d66">
                                            <template #activator="{ on, attrs }">
                                                <v-layout wrap v-on="on" v-bind="attrs">
                                                    <v-list-item-content>
                                                        <v-list-item-title>{{ item.category }}</v-list-item-title>
                                                    </v-list-item-content>
                                                </v-layout>
                                            </template>
                                            <span>{{ getDescriptionText(item) }}</span>
                                        </v-tooltip>
                                    </template>
                                </v-select>
                            </td>
                            <td v-if="findItemCategory('S10') || findItemCategory('S20')">
                                <v-select dense style="font-size:14px" v-model="item.classification"
                                    :items="itemClassificationItems" item-text="classification"
                                    item-value="classification" v-bind:no-data-text="$t('noData')"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    v-if="item.taxCategory === 'S20' || item.taxCategory === 'S10'">
                                </v-select>
                            </td>
                            <td>
                                <v-tooltip top color="#2b5d66">
                                    <template v-slot:activator="{ on }">
                                        <div v-on="on">
                                            <v-text-field dense style="font-size:14px" readonly v-model="item.total">
                                            </v-text-field>
                                        </div>
                                    </template>
                                    <span>{{ item.total }}</span>
                                </v-tooltip>
                            </td>
                            <td style="vertical-align:top">
                                <v-tooltip right color="#2b5d66">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-icon v-bind="attrs" v-on="on"
                                            v-show="newInvoice.items.length > 1 && (newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22)"
                                            @click="deleteRow(k)"
                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">mdi-delete</v-icon>
                                    </template>
                                    <span>{{ $t('deleteItem') }}</span>
                                </v-tooltip>
                            </td>
                        </tr>
                        <tr>
                            <!-- Original -->
                            <td class="table-empty" colspan="2">
                                <v-icon
                                    v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                    @click="addNewRow" color="#008B8B">control_point</v-icon> <span
                                    v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                    @click="addNewRow" style="cursor:pointer;color:#008B8B;">{{ $t('addItem') }}</span>
                            </td>
                            <!-- UC
            <td class="table-empty" colspan="2">
               <v-icon
               v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
               @click="addNewRow" color="#2b5d66">control_point</v-icon> <span v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22" @click="addNewRow" style="cursor:pointer;color:#2b5d66;">{{ $t('addItem') }}</span>
            </td> -->
                        </tr>
                        <tr>
                            <td colspan="12"><br /></td>
                        </tr>
                        <tr>
                            <td colspan="12"><br /></td>
                        </tr>
                        <tr
                            v-if="newInvoice.documentType.documentTypeId == 1 || newInvoice.documentType.documentTypeId == 5">
                            <td colspan="2" class="pr-4">
                                <v-combobox v-model="prepaymentInvoice" :items="prepaymentItems"
                                    item-text="prepaymentInvoice" item-value="prepaymentId"
                                    :search-input.sync="searchPrepayment" @change="addPrepayment"
                                    @click:clear="clearPrepayment" v-bind:label="$t('prepayment')" append-icon=""
                                    clearable
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                    @input.native="prepaymentInvoice = $event.srcElement.value">
                                </v-combobox>
                            </td>
                            <td colspan="3" class="pr-4">
                                <v-menu v-model="menuPrepaid" :close-on-content-click="false"
                                    transition="scale-transition" offset-y min-width="auto"
                                    :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field v-model="prepaymentInvoiceDueDate"
                                            v-bind:label="$t('paymentDate')" prepend-inner-icon="mdi-calendar" readonly
                                            clearable v-bind="attrs" v-on="on"
                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"></v-text-field>
                                    </template>
                                    <v-date-picker v-model="prepaymentInvoiceDueDate" no-title scrollable
                                        min="2017-01-01" max="2050-01-01" @input="menuPrepaid = false">
                                    </v-date-picker>
                                </v-menu>
                            </td>
                            <td colspan="7">
                                <v-icon
                                    v-show="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                    :disabled="!prepaymentInvoice || !prepaymentInvoice.length > 0 || !prepaymentInvoiceDueDate || !prepaymentInvoiceDueDate.length > 0"
                                    @click="addPrepayment" color="#2b5d66">control_point</v-icon> <span
                                    v-if="(newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22) && prepaymentInvoice && prepaymentInvoice.length > 0 && prepaymentInvoiceDueDate && prepaymentInvoiceDueDate.length > 0"
                                    @click="addPrepayment" style="cursor:pointer;color:#2b5d66;">{{ $t('addPrepayment')
                                    }}</span>
                                <span
                                    v-else-if="newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22"
                                    style="cursor:cursor;color:grey;">{{ $t('addPrepayment') }}</span>
                            </td>
                        </tr>
                        <tr v-if="newInvoice.prepayment.length > 0">
                            <td colspan="12">
                                <table style="border: 1px solid lightgray;font-size:12px;padding:10px">
                                    <thead>
                                        <td style="width:10%">{{ $t('invoiceNumber') }}</td>
                                        <td style="width:5.5%">S20</td>
                                        <td style="width:5.5%">{{ $t('vat') }} 20%</td>
                                        <td style="width:5.5%">S10</td>
                                        <td style="width:5.5%">{{ $t('vat') }} 10%</td>
                                        <td style="width:5.5%">AE20</td>
                                        <td style="width:5.5%">AE10</td>
                                        <td style="width:5.5%">O</td>
                                        <td style="width:5.5%">E</td>
                                        <td style="width:5.5%">R</td>
                                        <td style="width:5.5%">Z</td>
                                        <td style="width:5.5%">SS</td>
                                        <td style="width:5.5%">OE</td>
                                        <td style="width:5.5%">N</td>
                                        <td style="width:6.5%">{{ $t('total') }}</td>
                                        <td style="width:10%">{{ $t('date') }}</td>
                                        <td style="width:2%"></td>
                                    </thead>
                                    <tbody>
                                        <tr v-for="(prepayment, k) in newInvoice.prepayment" :key="k">
                                            <td style="width:10%;vertical-align:top">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            {{ prepayment.prepaymentInvoice }}
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.prepaymentInvoice }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.withoutVatS20"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.withoutVatS20 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.vatAmount20" readonly>
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.vatAmount20 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.withoutVatS10"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.withoutVatS10 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.vatAmount10" readonly>
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.vatAmount10 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountAE20"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountAE20 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountAE10"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountAE10 }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountO"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountO }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountE"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountE }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountR"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountR }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountZ"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountZ }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountSS"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountSS }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountOE"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountOE }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:5.5%">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            <v-text-field filled dense style="font-size:11px"
                                                                v-model="prepayment.amountN"
                                                                :rules="[v => /^(?!,$)[\d,.]+$/.test(v) || $t('number') + ' > 0']"
                                                                @input="calculatePrepaymentLineTotal(prepayment)"
                                                                @change="formatPrepayment(prepayment)"
                                                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">
                                                            </v-text-field>
                                                        </div>
                                                    </template>
                                                    <span>{{ prepayment.amountN }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:6.5%;vertical-align:top">
                                                <v-tooltip top color="#2b5d66">
                                                    <template v-slot:activator="{ on }">
                                                        <div v-on="on">
                                                            {{ formatNumberToString(prepayment.total) }} {{
                                                            newInvoice.currencyCode }}
                                                        </div>
                                                    </template>
                                                    <span>{{ formatNumberToString(prepayment.total) }}</span>
                                                </v-tooltip>
                                            </td>
                                            <td style="width:10%;vertical-align:top;">
                                                {{ prepayment.prepaymentInvoiceDueDate }}
                                            </td>
                                            <td style="width:2%;vertical-align:top">
                                                <v-tooltip right color="#2b5d66">
                                                    <template v-slot:activator="{ on, attrs }">
                                                        <v-icon v-bind="attrs" v-on="on"
                                                            v-show="newInvoice.prepayment.length > 0 && (newInvoice.statusId == 2 || newInvoice.statusId == 21 || newInvoice.statusId == 22)"
                                                            @click="deletePrepaymentRow(k)"
                                                            :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22">mdi-delete</v-icon>
                                                    </template>
                                                    <span>{{ $t('deletePrepayment') }}</span>
                                                </v-tooltip>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot style="text-align:right;font-size:14px;">
                        <tr
                            v-if="withoutVatItems10 > 0 || withoutVatItems20 > 0 || newInvoice.taxExemptionReasons.length > 0">
                            <td colspan="12">
                                <v-divider class="mb-10 mt-5"></v-divider>
                            </td>

                        </tr>
                    </tfoot>
                    <tr v-if="withoutVatItems10 > 0">
                        <td colspan="10">
                            {{ $t('sumOfItemsWithRate') }} 10%
                        </td>
                        <td colspan="2">
                            {{ formatNumberToString(withoutVatItems10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('sumOfItemsWithRate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(withoutVatItems20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-for="(reason, k) in newInvoice.taxExemptionReasons" :key="'W' + k">
                        <td v-show="reason.category.length > 0" colspan="10" style="text-align:right;">
                            {{ $t('sumOfItems') }} - {{ getDescription(reason.category) }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(reason.withoutVatItems) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr
                        v-if="withoutVatItems10 > 0 || withoutVatItems20 > 0 || newInvoice.taxExemptionReasons.length > 0">
                        <td colspan="12">
                            <v-divider class="mb-10 mt-5"></v-divider>
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems10 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('invoiceDiscount') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            <v-text-field filled dense
                                style="font-size:14px;width:85%;display: inline-block;text-align: right;"
                                v-model.trim="newInvoice.discountVatS10"
                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                @change="formatValue()" @input="calculateTotal()">
                            </v-text-field>
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('invoiceDiscount') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            <v-text-field filled dense
                                style="font-size:14px;width:85%;display: inline-block;text-align: right;"
                                v-model.trim="newInvoice.discountVatS20"
                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                @change="formatValue()" @input="calculateTotal()">
                            </v-text-field>
                        </td>
                    </tr>
                    <tr v-for="(reason, k) in newInvoice.taxExemptionReasons" :key="'D' + k">
                        <td v-show="reason.category.length > 0" colspan="10" style="text-align:right;">
                            {{ $t('invoiceDiscount') }} - {{ getDescription(reason.category) }}
                        </td>
                        <td v-show="reason.category.length > 0" colspan="2" style="text-align:right;">
                            <v-text-field filled dense
                                style="font-size:14px;width:85%;display: inline-block;text-align: right;"
                                v-model.trim="reason.discount"
                                :disabled="newInvoice.statusId != 2 && newInvoice.statusId != 21 && newInvoice.statusId != 22"
                                @change="formatValue(reason)" @input="calculateTotal()">
                            </v-text-field>
                        </td>
                    </tr>
                    <tr
                        v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD'">
                        <td colspan="12">
                            <v-divider class="mb-10 mt-5"></v-divider>
                        </td>
                    </tr>
                    <tr
                        v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD' && subtotalWithoutVatS10 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr
                        v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD' && subtotalWithoutVatS20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-show="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD'"
                        v-for="(reason, k) in newInvoice.taxExemptionReasons" :key="'S' + k">
                        <td v-show="reason.category.length > 0" colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ getDescription(reason.category) }}
                        </td>
                        <td v-show="reason.category.length > 0" colspan="2" style="text-align:right;">
                            {{ formatNumberToString(reason.subtotalWithoutVat) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr
                        v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD' && subtotalVatAmountS10 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr
                        v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0 && newInvoice.currencyCode == 'RSD' && subtotalVatAmountS20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="newInvoice.currencyCode != 'RSD'">
                        <td colspan="12">
                            <v-divider class="mb-10 mt-5"></v-divider>
                        </td>
                    </tr>
                    <tr v-if="this.newInvoice.currencyCode != 'RSD' && subtotalWithoutVatS10 > 0">
                        <td colspan="8" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS10) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS10RSD) }} RSD
                        </td>
                    </tr>
                    <tr v-if="newInvoice.currencyCode != 'RSD' && subtotalWithoutVatS20 > 0">
                        <td colspan="8" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS20) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalWithoutVatS20RSD) }} RSD
                        </td>
                    </tr>
                    <tr v-show="newInvoice.currencyCode != 'RSD'" v-for="(reason, k) in newInvoice.taxExemptionReasons"
                        :key="'SV' + k">
                        <td v-show="reason.category.length > 0" colspan="8" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ getDescription(reason.category) }}
                        </td>
                        <td v-show="reason.category.length > 0" colspan="2" style="text-align:right;">
                            {{ formatNumberToString(reason.subtotalWithoutVat) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td v-show="reason.category.length > 0" colspan="2" style="text-align:right;">
                            {{ formatNumberToString(reason.subtotalWithoutVatRSD) }} RSD
                        </td>
                    </tr>
                    <tr v-if="newInvoice.currencyCode != 'RSD' && subtotalVatAmountS10 > 0">
                        <td colspan="8" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS10) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS10RSD) }} RSD
                        </td>
                    </tr>
                    <tr v-if="newInvoice.currencyCode != 'RSD' && subtotalVatAmountS20 > 0">
                        <td colspan="8" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS20) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(subtotalVatAmountS20RSD) }} RSD
                        </td>
                    </tr>
                    <tr v-if="newInvoice.currencyCode != 'RSD' && subtotalVatAmountS20 > 0">
                        <td colspan="8" style="text-align:right;font-weight:bold;">
                            {{ $t('totalVat') }}
                        </td>
                        <td colspan="2" style="text-align:right;font-weight:bold;">
                            {{ formatNumberToString(subtotalVatAmount) }} {{ newInvoice.currencyCode }}
                        </td>
                        <td colspan="2" style="text-align:right;font-weight:bold;">
                            {{ formatNumberToString(subtotalVatAmountRSD) }} RSD
                        </td>
                    </tr>
                    <tr v-if="newInvoice.prepayment && newInvoice.prepayment.length > 0">
                        <td colspan="12">
                            <v-divider class="mb-10 mt-5"></v-divider>
                        </td>
                    </tr>
                    <tr v-if="prepaymentWithoutVat10 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentWithoutVat10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentWithoutVat20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentWithoutVat20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentVatAmount10 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentVat') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentVatAmount10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentVatAmount20 > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentVat') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentVatAmount20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="parseFloat(prepaymentAmountAE20) > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - promet po poreskoj stopi od 20% za koju je poreski dužnik
                            primalac
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountAE20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="parseFloat(prepaymentAmountAE10) > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - promet po poreskoj stopi od 10% za koju je poreski dužnik
                            primalac
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountAE10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountE > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - poresko oslobađanje bez prava na odbitak prethodnog poreza
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountE) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountO > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - nije predmet oporezivanja PDV 1
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountO) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountOE > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - nije predmet oporezivanja PDV 2
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountOE) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountR > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - izuzimanje od PDV-a
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountR) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountZ > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - poresko oslobađanje sa pravom na odbitak prethodnog poreza
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountZ) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountSS > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - posebni postupci oporezivanja
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountSS) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentAmountN > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('prepaymentBase') }} - anuliranje
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentAmountN) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentWithoutVatTotal > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalPrepaymentBase') }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentWithoutVatTotal) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentVatAmountTotal > 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalPrepaymentVat') }}
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(prepaymentVatAmountTotal) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="prepaymentTotal > 0">
                        <td colspan="10" style="text-align:right;font-weight:bold;">
                            {{ $t('totalPrepaymentAmount') }}
                        </td>
                        <td colspan="2" style="text-align:right;font-weight:bold;">
                            {{ formatNumberToString(prepaymentTotal) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr>
                        <td colspan="12">
                            <v-divider class="mb-10 mt-5"></v-divider>
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems10 != 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(withoutVatS10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems20 != 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(withoutVatS20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-for="(reason, k) in newInvoice.taxExemptionReasons" :key="k">
                        <td v-show="reason.category.length > 0" colspan="10" style="text-align:right;">
                            {{ $t('totalBase') }} - {{ getDescription(reason.category) }}
                        </td>
                        <td v-show="reason.category.length > 0" colspan="2" style="text-align:right;">
                            {{ formatNumberToString(reason.withoutVat) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems10 != 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 10%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(vatAmountS10) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr v-if="withoutVatItems20 != 0">
                        <td colspan="10" style="text-align:right;">
                            {{ $t('totalVat') }} - {{ $t('rate') }} 20%
                        </td>
                        <td colspan="2" style="text-align:right;">
                            {{ formatNumberToString(vatAmountS20) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                    <tr>
                        <td colspan="10" style="text-align:right;font-weight:bold;">
                            {{ $t('totalAmount') }}
                        </td>
                        <td colspan="2" style="text-align:right;font-weight:bold;">
                            {{ formatNumberToString(newInvoice.amount) }} {{ newInvoice.currencyCode }}
                        </td>
                    </tr>
                </table>
                <p></p>
            </v-form>
        </v-container>
        <InvoiceFooter :newInvoice="newInvoice" class="pa-2" />
        <TaxExemptionDialog v-if="showExamptionReason" />
        <UploadAttachment :newInvoice="newInvoice" v-if="showAttachment" />
        <OperationDialog :operations="newInvoice.operations" v-if="showOperation" />
    </div>
</template>

<script>
import moment from "moment"
import InvoiceFooter from "../components/NewInvoiceFooter.vue"
import TaxExemptionDialog from "../components/TaxExemptionReason.vue"
import UploadAttachment from "../components/UploadAttachment.vue"
import OperationDialog from "../components/OperationDialog.vue"

export default {
    name: "InvoiceForm",
    components: {
        InvoiceFooter,
        TaxExemptionDialog,
        UploadAttachment,
        OperationDialog
    },
    props: ['createNewInvoice', 'readInvoice', 'id', 'code'],
    data() {
        return {
            showAttachment: false,
            showOperation: false,
            initialInvoice: {
                id: -1,
                invoiceId: -1,
                invoice: "",
                statusId: 2,
                status: "Nacrt",
                statusEng: "Draft",
                documentType: "",
                currencyCode: "",
                exchangeRate: "",
                contractDocumentReference: "",
                customer: {},
                taxExemptionReasons: [],
                prepayment: [],
                modelNumber: "",
                referenceNumber: "",
                purchaseOrder: "",
                originatorDocumentReference: "",
                penaltyAmount: "",
                startDate: null,
                endDate: null,
                vatDate: null,
                deliveryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                dueDate: new Date(Date.now() + (3600 * 1000 * 360) - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                items: [{
                    code: "",
                    name: "",
                    price: this.formatNumberToString4Dig(0),
                    quantity: this.formatNumberToString(1),
                    unitMeasure: "H87",
                    discount: this.formatNumberToString(0),
                    discountAmount: this.formatNumberToString(0),
                    amountWithoutVat: this.formatNumberToString(0),
                    vat: "20",
                    taxCategory: "S20",
                    total: this.formatNumberToString(0),
                    classification: ""
                }],
                amount: 0,
                subtotal: 0,
                discount: 0,
                vat: 0,
                files: [],
                billingReferences: [],
                note: "",
                discountVatS10: "",
                discountVatS20: "",
                rejectionNote: "",
                user: "",
                cir: "",
                billingReferenceTypeId: -1,
                buyerReference: "",
                objectReference: "",
                operations: [],
                despatchReferences: [],
                contractDate: null
            },
            newInvoice: {
                id: -1,
                invoiceId: -1,
                invoice: "",
                statusId: 2,
                status: "Nacrt",
                statusEng: "Draft",
                documentType: "",
                currencyCode: "",
                exchangeRate: "",
                contractDocumentReference: "",
                customer: {},
                taxExemptionReasons: [],
                prepayment: [],
                modelNumber: "",
                referenceNumber: "",
                purchaseOrder: "",
                originatorDocumentReference: "",
                penaltyAmount: "",
                vatDate: null,
                startDate: null,
                endDate: null,
                deliveryDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                dueDate: new Date(Date.now() + (3600 * 1000 * 360) - new Date().getTimezoneOffset() * 60000)
                    .toISOString()
                    .substr(0, 10),
                items: [{
                    code: "",
                    name: "",
                    price: this.formatNumberToString4Dig(0),
                    quantity: this.formatNumberToString(1),
                    unitMeasure: "H87",
                    discount: this.formatNumberToString(0),
                    discountAmount: this.formatNumberToString(0),
                    amountWithoutVat: this.formatNumberToString(0),
                    vat: "20",
                    taxCategory: "S20",
                    total: this.formatNumberToString(0),
                    classification: ""
                }],
                amount: 0,
                subtotal: 0,
                discount: 0,
                vat: 0,
                files: [],
                billingReferences: [],
                note: "",
                discountVatS10: "",
                discountVatS20: "",
                rejectionNote: "",
                user: "",
                cir: "",
                billingReferenceTypeId: -1,
                buyerReference: "",
                objectReference: "",
                operations: [],
                despatchReferences: [],
                contractDate: null
            },
            taxExemptionReasonNumberLabel: "Broj rešenja o oslobođenju",
            invoicesNumbers: [],
            vatDateItems: [],
            valid: true,
            menuStart: false,
            menuEnd: false,
            menuDelivery: false,
            menuDue: false,
            menuPrepaid: false,
            menuContract: false,
            disableVat: false,
            cbVat: "",
            showExamptionReason: false,
            showExamptionReasonNumber: false,
            examptionReasonEItems: [],
            examptionReasonOItems: [],
            examptionReasonAEItems: [],
            examptionRessonCategoryItems: [],
            law: "",
            article: "",
            paragraph: "",
            text: "",
            search: null,
            searchPrepaymentBillingReference: null,
            searchPrepayment: null,
            currencies: [],
            billingReferenceTypeItems: [],
            billingReference: null,
            billingReferencesItems: [],
            prepaymentItems: [],
            currentFile: [],
            unitMeasureItems: [],
            withoutVatItems10: 0,
            withoutVatItems20: 0,
            withoutVatItemsE: 0,
            withoutVatItemsO: 0,
            withoutVatItemsAE20: 0,
            withoutVatItemsAE10: 0,
            withoutVatS10: 0,
            withoutVatS20: 0,
            withoutVatE: 0,
            withoutVatO: 0,
            withoutVatAE20: 0,
            withoutVatAE10: 0,
            vatAmountS10: 0,
            vatAmountS20: 0,
            predefinedItemValues: null,
            allTaxExemptionReason: null,
            taxCategoryItems: [],
            itemsCodes: [],
            itemClassificationItems: [],
            prepaymentInvoice: "",
            prepaymentInvoiceDueDate: "",
            prepaymentWithoutVat20: 0,
            prepaymentVatAmount20: 0,
            prepaymentWithoutVat10: 0,
            prepaymentVatAmount10: 0,
            prepaymentAmountAE20: 0,
            prepaymentAmountAE10: 0,
            prepaymentAmountO: 0,
            prepaymentAmountE: 0,
            prepaymentAmountR: 0,
            prepaymentAmountZ: 0,
            prepaymentAmountSS: 0,
            prepaymentAmountOE: 0,
            prepaymentAmountN: 0,
            prepaymentWithoutVatTotal: 0,
            prepaymentVatAmountTotal: 0,
            prepaymentTotal: 0,
            subtotalWithoutVatS10: 0,
            subtotalWithoutVatS20: 0,
            subtotalVatAmountS10: 0,
            subtotalVatAmountS20: 0,
            subtotalVatAmount: 0,
            subtotalWithoutVatS10RSD: 0,
            subtotalWithoutVatS20RSD: 0,
            subtotalVatAmountS10RSD: 0,
            subtotalVatAmountS20RSD: 0,
            subtotalVatAmountRSD: 0,
            foundNumber: false,
            despatchReference: null,
            despatchReferencesItems: []
        }
    },
    async beforeRouteLeave(to, from, next) {
        if (this.hasChanged && to.name != 'Login' && (this.newInvoice.statusId == 2 || this.newInvoice.statusId == 21 || this.newInvoice.statusId == 19 || this.newInvoice.statusId == 22) && !await this.$confirm(this.$t('hasChanges'), { title: this.$t('warning'), buttonTrueText: this.$t('yes'), buttonFalseText: this.$t('no'), persistent: true })) {
            next(false)
        } else {
            next()
        }
    },
    created() {
        console.log(parseFloat(this.convertFormattedStringToNumber('0,01')))
        this.invoiceData();
        window.addEventListener('beforeunload', this.beforeWindowUnload)
    },
    beforeDestroy() {
        window.removeEventListener('beforeunload', this.beforeWindowUnload)
    },
    watch: {
        search(val) {
            val && val.length > 0 && val !== this.newInvoice.billingReferences && this.getBillingReferences()
        },
        searchPrepaymentBillingReference(val) {
            console.log('searchPrepaymentBillingReference')
            console.log(val)
            val && val.length > 0 && val !== this.newInvoice.billingReferences && this.getPrepaymentBillingReferences()
        },
        searchPrepayment(val) {
            val && val.length > 0 && val !== this.newInvoice.prepayment.prepaymentInvoice && this.getPrepayment()
        }
    },
    computed: {
        hasChanged() {
            return JSON.stringify(this.newInvoice) !== JSON.stringify(this.initialInvoice)
        },
        loggedUser() {
            return this.$store.getters.loggedUser;
        },
        selectedOrganization() {
            return this.$store.getters.organizationId
        }
    },
    methods: {
        sendToValidationPermission() {
            const isFound = this.loggedUser.permission.some(element => {
                if (element.permission === 'SendSalesInvoiceToValidation' && element.organizationId === this.selectedOrganization)
                    return true;
            });
            return isFound;
        },
        validationPermission() {
            const isFound = this.loggedUser.permission.some(element => {
                if (element.permission === 'Validation' && element.organizationId === this.selectedOrganization)
                    return true;
            });
            return isFound;
        },
        invoiceNumberRule() {
            const rules = [v => !!v || this.sendToValidationPermission() || this.$t('invoiceNumberRequired'), v => (v && v.length > 2) || this.sendToValidationPermission() || this.$t('invoiceNumberMin')]

            if (this.foundNumber) {
                const rule = this.$t('invoiceNumberExist')
                rules.push(rule)
            }
            return rules
        },
        contractRule() {
            const rules = []
            var jbkjs = this.getCustomerJbkjs()
            if (jbkjs != null && jbkjs.length > 0) {
                if (this.newInvoice.purchaseOrder.length == 0) {
                    const rule = v => !!v || this.$t('contractNumberRequired')
                    rules.push(rule)
                }
            }
            return rules
        },
        purchaseOrderRule() {
            const rules = []
            var jbkjs = this.getCustomerJbkjs()
            if (jbkjs != null && jbkjs.length > 0) {
                if (this.newInvoice.contractDocumentReference.length == 0) {
                    const rule = v => !!v || this.$t('purchaseOrderRequired')
                    rules.push(rule)
                }
            }
            return rules
        },
        modelNumberRule() {
            const rules = []
            if (this.newInvoice.modelNumber.length > 0) {
                const rule = v => (!isNaN(parseFloat(v)) && v >= 0 && v <= 99) || this.$t('modelRule')
                rules.push(rule)
            }
            return rules
        },
        taxExemptionReasonRule() {
            const rules = []
            if (this.newInvoice.taxExemptionReasonE && this.newInvoice.taxExemptionReasonE.key.length > 0 && this.newInvoice.taxExemptionReasonE.key != "PDV-RS-25-2-1") {
                const rule = v => !!v || 'Broj rešenja je obavezan'
                rules.push(rule)
            }
            return rules
        },
        invoicePeriodStartRule() {
            const rules = []
            if (this.newInvoice.endDate && this.newInvoice.endDate.length > 0) {
                const rule = v => !!v || this.$t('startDateRequired')
                rules.push(rule)
            }

            if (this.newInvoice.endDate && this.newInvoice.endDate.length > 0) {
                const rule = v => (!!v && moment(this.newInvoice.startDate).isBefore(this.newInvoice.endDate, 'day')) || this.$t('dateRule')
                rules.push(rule)
            }
            return rules
        },
        invoicePeriodEndRule() {
            const rules = []
            if (this.newInvoice.startDate && this.newInvoice.startDate.length > 0) {
                const rule = v => !!v || this.$t('endDateRequired')
                rules.push(rule)
            }
            return rules
        },
        invoiceContractDateRule() {
            const rules = [v => !!v || this.$t('contractDateRequired')]
            return rules
        },
        getInvoiceNumberLabel() {
            if (this.newInvoice.documentType.documentTypeId == 1 || this.newInvoice.documentType.documentTypeId == 5)
                return this.$t('invoiceNum') + "*"
            if (this.newInvoice.documentType.documentTypeId == 2 || this.newInvoice.documentType.documentTypeId == 6)
                return this.$t('creditNoteNum') + "*"
            if (this.newInvoice.documentType.documentTypeId == 3 || this.newInvoice.documentType.documentTypeId == 7)
                return this.$t('debitNoteNum') + "*"
            if (this.newInvoice.documentType.documentTypeId == 4 || this.newInvoice.documentType.documentTypeId == 8)
                return this.$t('prepaymentNum') + "*"
            return ""
        },
        getDueDateLabel() {
            if (this.newInvoice.documentType.documentTypeId == 4 || this.newInvoice.documentType.documentTypeId == 8)
                return this.$t('paymentDate') + "*"
            return this.$t('dueDate') + "*"
        },
        getContractLabel() {
            if (this.newInvoice.customer.jbkjs != null && this.newInvoice.customer.jbkjs.length > 0)
                return this.$t('contractNumber') + "*"
            return this.$t('contractNumber')
        },
        getBillingReferenceLabel() {
            if (this.newInvoice.documentType.documentTypeId == 2 || this.newInvoice.documentType.documentTypeId == 6)
                return this.$t('creditNoteRefersTo')
            if (this.newInvoice.documentType.documentTypeId == 3 || this.newInvoice.documentType.documentTypeId == 7)
                return this.$t('debitNoteRefersTo')
            return ""
        },
        getTaxExemptionReasonNumberLabel() {
            if (this.newInvoice.taxExemptionReason.key != "reasonKey_9")
                return this.taxExemptionReasonNumberLabel + "*"
            return this.taxExemptionReasonNumberLabel
        },
        getCustomerJbkjs() {
            if (this.createNewInvoice != undefined)
                return this.createNewInvoice.customer.jbkjs
            else if (this.newInvoice != undefined)
                return this.newInvoice.customer.jbkjs
            else
                return ''
        },
        fileCount() {
            return this.newInvoice.files.length
        },
        getVatDates() {
            if (this.newInvoice.documentType.documentTypeId != 2 && this.newInvoice.documentType.documentTypeId != 6) {
                this.axios.get('InvoiceUtilities/GetVatDates?documentTypeId=' + this.newInvoice.documentType.documentTypeId).then((response) => {
                    this.vatDateItems = response.data
                    if (!this.newInvoice.vatDate)
                        this.newInvoice.vatDate = this.vatDateItems[0]
                })
                    .catch(err => console.log(err))
            }
        },
        checkInvoiceNumber() {
            if (this.newInvoice.invoice.length > 3)
                this.axios.get('SalesInvoices/CheckInvoiceNumber?organizationId=' + this.$store.getters.organizationId + '&id=' + this.newInvoice.id + '&number=' + this.newInvoice.invoice).then((response) => {
                    this.foundNumber = response.data
                })
                    .catch(err => console.log(err))
        },
        getBillingReferences() {
            this.axios.get('SalesInvoices/GetSalesInvoiceBillingReference?organizationId=' + this.$store.getters.organizationId + "&customerId=" + this.newInvoice.customer.organizationId + "&invoiceNumber=" + this.search).then((response) => {
                this.billingReferencesItems = response.data
            })
                .catch(err => console.log(err))
        },
        getPrepaymentBillingReferences() {
            this.axios.get('SalesInvoices/GetSalesPrepaymentInvoiceBillingReference?organizationId=' + this.$store.getters.organizationId + "&customerId=" + this.newInvoice.customer.organizationId + "&invoiceNumber=" + this.searchPrepaymentBillingReference).then((response) => {
                this.billingReferencesItems = response.data
            })
                .catch(err => console.log(err))
        },
        getPrepayment() {
            this.axios.get('SalesInvoices/GetSalesInvoicePrepayment?organizationId=' + this.$store.getters.organizationId + "&customerId=" + this.newInvoice.customer.organizationId + "&invoiceNumber=" + this.searchPrepayment).then((response) => {
                this.prepaymentItems = response.data
            })
                .catch(err => console.log(err))
        },
        getItemsCodes() {
            this.itemsCodes = this.loggedUser.itemCode.filter(c => { return c.organizationId == this.selectedOrganization || c.organizationId == -1 });
            if (this.newInvoice.documentType.documentTypeId >= 5) {
                if (this.code != null) {
                    this.itemsCodes = this.itemsCodes.filter(c => { return c.code === this.code });
                    this.newInvoice.items[0].code = this.code;
                }
                else
                    this.itemsCodes = this.itemsCodes.filter(c => { return c.code === this.newInvoice.items[0].code });
            }
            else if (this.itemsCodes.length > 1)
                this.itemsCodes = this.itemsCodes.slice(1);
        },
        getInvoiceDetails(item) {
            this.axios.get('SalesInvoices/GetSalesInvoiceDetails?invoiceId=' + item.id).then((response) => {
                return response.data
            })
                .catch(err => console.log(err));
        },
        getBillingReferenceTypes() {
            this.axios.get('InvoiceUtilities/GetBillingReferenceType').then((response) => {
                this.billingReferenceTypeItems = response.data
                if (this.newInvoice.documentType.documentTypeId == 3 || this.newInvoice.documentType.documentTypeId == 7)
                    this.billingReferenceTypeItems = this.billingReferenceTypeItems.filter(t => { return t.id < 3 })
                if (this.newInvoice.billingReferenceTypeId < 1 && (this.newInvoice.documentType.documentTypeId == 2 || this.newInvoice.documentType.documentTypeId == 3 || this.newInvoice.documentType.documentTypeId == 6 || this.newInvoice.documentType.documentTypeId == 7))
                    this.newInvoice.billingReferenceTypeId = 2
            })
                .catch(err => console.log(err));
        },
        getRuleText(category) {
            if (category != 'N')
                return this.$t('number') + ' > 0'
            else
                return '-1'
        },
        getDescription(category) {
            if (category.length > 0) {
                let cat = this.examptionRessonCategoryItems.filter(c => { return c.category === category })
                if (cat.length > 0) {
                    if (localStorage.getItem('language') === 'eng')
                        return cat[0].descriptionEng;
                    else
                        return cat[0].description
                }
            }
        },
        findCategory(category) {
            const isFound = this.newInvoice.taxExemptionReasons.some(element => {
                if (element.category === category)
                    return true;
            });
            return isFound;
        },
        findItemCategory(category) {
            console.log(category);
            var isFound = false;
            isFound = this.newInvoice.items.some(element => {
                if (element.taxCategory === category)
                    return true;
            });
            console.log("findItemCategory");
            console.log(isFound);
            return isFound;
        },
        categoryExists(r) {
            let filtered = this.newInvoice.taxExemptionReasons.filter(reason => { return reason.category === r.category });
            return filtered.length
        },
        getTaxExemptionReasons() {
            this.axios.get('InvoiceUtilities/GetTaxExemptionReasons').then((response) => {
                let reasons = response.data;
                this.allTaxExemptionReason = response.data
                this.examptionReasonEItems = reasons.filter(reason => { return reason.category === 'E' });
                this.examptionReasonOItems = reasons.filter(reason => { return reason.category === 'O' });
                this.examptionReasonAEItems = reasons.filter(reason => { return reason.category === 'AE' });
                //this.examptionRessonCategoryItems.push(...[{category:'S', text: 'Standardna'}, {category:'E', text: 'Izuzeto od PDV-a'}, {category:'O', text: 'Ne podleže primeni PDV-a'}, {category:'AE', text: 'Obrnuto obračunavanje PDV-a'}])
            })
                .catch(err => console.log(err));
        },
        getTaxCategory() {
            this.axios.get('InvoiceUtilities/GetTaxCategory').then((response) => {
                this.taxCategoryItems = response.data;
                this.examptionRessonCategoryItems = this.taxCategoryItems.filter(category => { return category.category != 'S20' && category.category != 'S10' });
            })
                .catch(err => console.log(err));
        },
        getItemClassification() {
            this.axios.get('InvoiceUtilities/GetItemClassification').then((response) => {
                this.itemClassificationItems = response.data;
            })
                .catch(err => console.log(err));
        },
        addTax() {
            this.newInvoice.taxExemptionReasons.push({
                key: "",
                law: "",
                article: "",
                paragraph: "",
                point: "",
                subpoint: "",
                text: "",
                category: ""
            })
        },
        deleteTaxExemptionReason(index) {
            if (this.newInvoice.taxExemptionReasons.length > 0) {
                if (index > -1) {
                    this.newInvoice.taxExemptionReasons.splice(index, 1);
                }
            }
        },
        getTaxExemptionReasonForCategory(category) {
            if (this.allTaxExemptionReason != null)
                return this.allTaxExemptionReason.filter(reason => { return reason.category === category });
        },
        getUnitMeasure() {
            this.axios.get('InvoiceUtilities/GetUnitMeasures').then((response) => {
                this.unitMeasureItems = response.data
            })
                .catch(err => console.log(err));
        },
        getCurrencies() {
            this.axios.get('InvoiceUtilities/GetCurrency').then((response) => {
                this.currencies = response.data
                if (this.newInvoice.currencyCode.length == 0)
                    this.newInvoice.currencyCode = 'RSD'
            })
                .catch(err => console.log(err));
        },
        addNewRow() {
            if (this.predefinedItemValues) {
                const init = {
                    code: "",
                    name: "",
                    price: this.formatNumberToString4Dig(0),
                    quantity: this.formatNumberToString(1),
                    unitMeasure: "H87",
                    discount: this.formatNumberToString(0),
                    discountAmount: this.formatNumberToString(0),
                    amountWithoutVat: this.formatNumberToString(0),
                    vat: "20",
                    taxCategory: "S20",
                    total: this.formatNumberToString(0),
                    classification: ""
                }
                const prf = this.predefinedItemValues[0]
                for (const property in init) {
                    if (prf[property] != null && prf[property] != 0) {
                        init[property] = prf[property]
                    }
                }
                this.newInvoice.items.push(init);
            }
            else {
                this.newInvoice.items.push({
                    code: "",
                    name: "",
                    price: this.formatNumberToString4Dig(0),
                    quantity: this.formatNumberToString(1),
                    unitMeasure: "H87",
                    discount: this.formatNumberToString(0),
                    discountAmount: this.formatNumberToString(0),
                    amountWithoutVat: this.formatNumberToString(0),
                    vat: "20",
                    taxCategory: "S20",
                    total: this.formatNumberToString(0)
                });
            }
        },
        addPrepayment() {
            if (this.prepaymentInvoice.prepaymentId && this.prepaymentInvoice.prepaymentId > 0) {
                const found = this.newInvoice.prepayment.some(el => el.prepaymentInvoiceId === this.prepaymentInvoice.prepaymentId);
                if (!found)
                    this.newInvoice.prepayment.push({
                        prepaymentInvoiceId: this.prepaymentInvoice.prepaymentId,
                        prepaymentInvoice: this.prepaymentInvoice.prepaymentInvoice,
                        prepaymentInvoiceDueDate: this.prepaymentInvoice.prepaymentInvoiceDueDate,
                        withoutVatS20: this.formatNumberToString(0),
                        vatAmount20: this.formatNumberToString(0),
                        withoutVatS10: this.formatNumberToString(0),
                        vatAmount10: this.formatNumberToString(0),
                        amountE: this.formatNumberToString(0),
                        amountO: this.formatNumberToString(0),
                        amountAE20: this.formatNumberToString(0),
                        amountAE10: this.formatNumberToString(0),
                        amountR: this.formatNumberToString(0),
                        amountZ: this.formatNumberToString(0),
                        amountSS: this.formatNumberToString(0),
                        amountOE: this.formatNumberToString(0),
                        amountN: this.formatNumberToString(0),
                        total: 0
                    })
            }
            else {
                const found = this.newInvoice.prepayment.some(el => el.prepaymentInvoice === this.prepaymentInvoice);
                if (!found)
                    this.newInvoice.prepayment.push({
                        prepaymentInvoiceId: -1,
                        prepaymentInvoice: this.prepaymentInvoice,
                        prepaymentInvoiceDueDate: this.prepaymentInvoiceDueDate,
                        withoutVatS20: this.formatNumberToString(0),
                        vatAmount20: this.formatNumberToString(0),
                        withoutVatS10: this.formatNumberToString(0),
                        vatAmount10: this.formatNumberToString(0),
                        amountE: this.formatNumberToString(0),
                        amountO: this.formatNumberToString(0),
                        amountAE20: this.formatNumberToString(0),
                        amountAE10: this.formatNumberToString(0),
                        amountR: this.formatNumberToString(0),
                        amountZ: this.formatNumberToString(0),
                        amountSS: this.formatNumberToString(0),
                        amountOE: this.formatNumberToString(0),
                        amountN: this.formatNumberToString(0),
                        total: 0
                    })
            }
            this.prepaymentInvoice = ""
            this.prepaymentInvoiceDueDate = ""
        },
        deletePrepaymentRow(index) {
            if (this.newInvoice.prepayment.length > 0) {
                if (index > -1) {
                    this.newInvoice.prepayment.splice(index, 1);
                    this.calculateTotal()
                }
            }
        },
        clearPrepaymentInvoice() {
            this.prepaymentInvoice = ""
        },
        clearPrepaymentInvoiceDueDate() {
            this.prepaymentInvoiceDueDate = ""
        },
        setDefaultValue(index) {
            if (this.newInvoice.items[index].discountAmount.length == 0) {
                this.newInvoice.items[index].discountAmount = this.formatNumberToString(0)
            }
            if (this.newInvoice.items[index].discount.length == 0) {
                this.newInvoice.items[index].discount = this.formatNumberToString(0)
            }
        },
        clearDiscountAmount(item) {
            if (item.discount.length == 0) {
                item.discountAmount = this.formatNumberToString(0)
                this.calculateLineTotal(item)
            }
        },
        clearDiscount(index) {
            this.newInvoice.items[index].discount = this.formatNumberToString(0)
        },
        deleteRow(index) {
            if (this.newInvoice.items.length > 1) {
                if (index > -1) {
                    var category = this.newInvoice.items[index].taxCategory;
                    var vat = this.newInvoice.items[index].vat;
                    this.newInvoice.items.splice(index, 1);
                    if (category == 'S10' && vat == 10 && this.withoutVat10 == 0)
                        this.discountVatS10 = ''
                    if (category == 'S20' && vat == 20 && this.withoutVat10 == 0)
                        this.discountVatS20 = ''
                }
                this.calculateTotal();
            }
        },
        calculateTotal() {
            var subtotal, total, vatAmount
            if (this.newInvoice.prepayment && this.newInvoice.prepayment.length > 0) {
                this.calculatePrepaymentTotal()
            }

            let items10 = this.newInvoice.items.filter(item => { return item.vat == 10 })
            subtotal = items10.reduce(function (sum, product) {
                var value = product.amountWithoutVat
                while (value.includes(".")) {
                    value = value.replace(".", "")
                }
                value = value.replace(",", ".")
                var lineAmountWithoutVat = parseFloat(value)
                if (!isNaN(lineAmountWithoutVat)) {
                    return sum + lineAmountWithoutVat
                }
            }, 0);
            this.withoutVatItems10 = subtotal.toFixed(2)

            let items20 = this.newInvoice.items.filter(item => { return item.vat == 20 })
            subtotal = items20.reduce(function (sum, product) {
                var value = product.amountWithoutVat
                while (value.includes(".")) {
                    value = value.replace(".", "")
                }
                value = value.replace(",", ".")
                var lineAmountWithoutVat = parseFloat(value)
                if (!isNaN(lineAmountWithoutVat)) {
                    return sum + lineAmountWithoutVat
                }
            }, 0);

            this.withoutVatItems20 = subtotal.toFixed(2)

            this.newInvoice.taxExemptionReasons.forEach(reason => {
                let itemsCategory = this.newInvoice.items.filter(item => { return item.taxCategory == reason.category })
                subtotal = itemsCategory.reduce(function (sum, product) {
                    var value = product.amountWithoutVat
                    while (value.includes(".")) {
                        value = value.replace(".", "")
                    }
                    value = value.replace(",", ".")
                    var lineAmountWithoutVat = parseFloat(value)
                    if (!isNaN(lineAmountWithoutVat)) {
                        return sum + lineAmountWithoutVat
                    }
                }, 0);
                reason.withoutVatItems = subtotal.toFixed(2)
            })

            //this.newInvoice.subtotal = subtotal.toFixed(2)

            if (this.newInvoice.discountVatS10.length > 0 && parseFloat(this.withoutVatItems10) > 0)
                this.subtotalWithoutVatS10 = (this.withoutVatItems10 - this.convertFormattedStringToNumber(this.newInvoice.discountVatS10)).toFixed(2)
            else {
                this.subtotalWithoutVatS10 = this.withoutVatItems10
                this.newInvoice.discountVatS10 = ''
            }

            if (this.newInvoice.discountVatS20.length > 0 && parseFloat(this.withoutVatItems20) > 0)
                this.subtotalWithoutVatS20 = (this.withoutVatItems20 - this.convertFormattedStringToNumber(this.newInvoice.discountVatS20)).toFixed(2)
            else {
                this.subtotalWithoutVatS20 = this.withoutVatItems20
                this.newInvoice.discountVatS20 = ''
            }

            this.newInvoice.taxExemptionReasons.forEach(reason => {
                if (reason.discount && reason.discount.length > 0)
                    reason.subtotalWithoutVat = (reason.withoutVatItems - this.convertFormattedStringToNumber(reason.discount)).toFixed(2)
                else
                    reason.subtotalWithoutVat = reason.withoutVatItems
            })

            this.subtotalVatAmountS10 = (this.subtotalWithoutVatS10 * 0.1).toFixed(2)
            this.subtotalVatAmountS20 = (this.subtotalWithoutVatS20 * 0.2).toFixed(2)

            if (this.newInvoice.currencyCode != 'RSD') {
                this.subtotalWithoutVatS10RSD = (this.subtotalWithoutVatS10 * this.convertFormattedStringToNumber(this.newInvoice.exchangeRate)).toFixed(2)
                this.subtotalWithoutVatS20RSD = (this.subtotalWithoutVatS20 * this.convertFormattedStringToNumber(this.newInvoice.exchangeRate)).toFixed(2)
                this.subtotalVatAmountS10RSD = (this.subtotalVatAmountS10 * this.convertFormattedStringToNumber(this.newInvoice.exchangeRate)).toFixed(2)
                this.subtotalVatAmountS20RSD = (this.subtotalVatAmountS20 * this.convertFormattedStringToNumber(this.newInvoice.exchangeRate)).toFixed(2)
                this.subtotalVatAmount = parseFloat(this.subtotalVatAmountS10) + parseFloat(this.subtotalVatAmountS20)
                this.subtotalVatAmountRSD = parseFloat(this.subtotalVatAmountS10RSD) + parseFloat(this.subtotalVatAmountS20RSD)

                this.newInvoice.taxExemptionReasons.forEach(reason => {
                    reason.subtotalWithoutVatRSD = (reason.subtotalWithoutVat * this.convertFormattedStringToNumber(this.newInvoice.exchangeRate)).toFixed(2)
                })
            }

            if (this.newInvoice.prepayment && this.newInvoice.prepayment.length > 0) {

                this.withoutVatS10 = parseFloat(this.subtotalWithoutVatS10 - this.prepaymentWithoutVat10).toFixed(2)
                this.withoutVatS20 = parseFloat(this.subtotalWithoutVatS20 - this.prepaymentWithoutVat20).toFixed(2)
                this.vatAmountS10 = parseFloat(this.subtotalVatAmountS10 - this.prepaymentVatAmount10).toFixed(2)
                this.vatAmountS20 = parseFloat(this.subtotalVatAmountS20 - this.prepaymentVatAmount20).toFixed(2)

                this.newInvoice.taxExemptionReasons.forEach(reason => {
                    if (reason.category == 'AE20')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountAE20).toFixed(2)
                    if (reason.category == 'AE10')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountAE10).toFixed(2)
                    else if (reason.category == 'O')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountO).toFixed(2)
                    else if (reason.category == 'E')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountE).toFixed(2)
                    else if (reason.category == 'R')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountR).toFixed(2)
                    else if (reason.category == 'Z')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountZ).toFixed(2)
                    else if (reason.category == 'SS')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountSS).toFixed(2)
                    else if (reason.category == 'OE')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountOE).toFixed(2)
                    else if (reason.category == 'N')
                        reason.withoutVat = parseFloat(reason.subtotalWithoutVat - this.prepaymentAmountN).toFixed(2)
                })
            }
            else {
                this.withoutVatS10 = parseFloat(this.subtotalWithoutVatS10).toFixed(2)
                this.withoutVatS20 = parseFloat(this.subtotalWithoutVatS20).toFixed(2)
                this.vatAmountS10 = parseFloat(this.subtotalVatAmountS10).toFixed(2)
                this.vatAmountS20 = parseFloat(this.subtotalVatAmountS20).toFixed(2)

                this.newInvoice.taxExemptionReasons.forEach(reason => {
                    reason.withoutVat = parseFloat(reason.subtotalWithoutVat).toFixed(2)
                })
            }

            vatAmount = Number(this.vatAmountS10) + Number(this.vatAmountS20)
            this.newInvoice.vat = vatAmount.toFixed(2)

            let reasonTotal = this.newInvoice.taxExemptionReasons.reduce(function (sum, reason) {
                return sum + Number(reason.withoutVat)
            }, 0)

            total = Number(this.withoutVatS10) + Number(this.vatAmountS10) + Number(this.withoutVatS20) + Number(this.vatAmountS20) + reasonTotal //+ Number(this.withoutVatS20) + Number(this.vatAmountS20) + Number(this.withoutVatE) + Number(this.withoutVatO) + Number(this.withoutVatAE)
            this.newInvoice.amount = total.toFixed(2)

            this.newInvoice.subtotal = total - vatAmount
        },
        calculateLineTotal(item) {
            var withoutVat = this.convertFormattedStringToNumber(item.price) * this.convertFormattedStringToNumber(item.quantity)
            if (!isNaN(withoutVat)) {
                if (item.discount != undefined && this.convertFormattedStringToNumber(item.discount) > 0) {
                    item.discountAmount = parseFloat(withoutVat * (this.convertFormattedStringToNumber(item.discount) / 100)).toFixed(2)
                    withoutVat = withoutVat - item.discountAmount
                    item.discountAmount = this.formatNumberToString(item.discountAmount)
                }
                else if (item.discountAmount != undefined && this.convertFormattedStringToNumber(item.discountAmount) > 0) {
                    withoutVat = withoutVat - this.convertFormattedStringToNumber(item.discountAmount)
                }

                item.amountWithoutVat = this.formatNumberToString(withoutVat)

                if (item.vat != undefined) {
                    var lineTotal = withoutVat + withoutVat * (item.vat / 100)
                    item.total = this.formatNumberToString(lineTotal)
                }
                else {
                    item.total = this.formatNumberToString(withoutVat)
                }
            }
            else {
                //item.discountAmount = item.discountAmount
                item.amountWithoutVat = this.formatNumberToString(0)
                item.total = this.formatNumberToString(0)
            }
            this.calculateTotal();
        },
        calculatePrepaymentLineTotal(item) {
            if (item.withoutVatS20.length == 0) {
                item.withoutVatS20 = this.formatNumberToString(0)
                item.vatAmount20 = this.formatNumberToString(0)
            }
            else if (this.convertFormattedStringToNumber(item.withoutVatS20) > 0)
                item.vatAmount20 = this.formatNumberToString(this.convertFormattedStringToNumber(item.withoutVatS20) * 0.2)
            else
                item.vatAmount20 = this.formatNumberToString(0)

            if (item.withoutVatS10.length == 0) {
                item.withoutVatS10 = this.formatNumberToString(0)
                item.vatAmount10 = this.formatNumberToString(0)
            }
            if (this.convertFormattedStringToNumber(item.withoutVatS10) > 0)
                item.vatAmount10 = this.formatNumberToString(this.convertFormattedStringToNumber(item.withoutVatS10) * 0.1)
            else
                item.vatAmount10 = this.formatNumberToString(0)

            if (item.amountAE20.length == 0)
                item.amountAE20 = this.formatNumberToString(0)
            if (item.amountAE10.length == 0)
                item.amountAE10 = this.formatNumberToString(0)
            if (item.amountE.length == 0)
                item.amountE = this.formatNumberToString(0)
            if (item.amountN.length == 0)
                item.amountN = this.formatNumberToString(0)
            if (item.amountO.length == 0)
                item.amountO = this.formatNumberToString(0)
            if (item.amountOE.length == 0)
                item.amountOE = this.formatNumberToString(0)
            if (item.amountR.length == 0)
                item.amountR = this.formatNumberToString(0)
            if (item.amountSS.length == 0)
                item.amountSS = this.formatNumberToString(0)
            if (item.amountZ.length == 0)
                item.amountZ = this.formatNumberToString(0)

            item.total = parseFloat(this.convertFormattedStringToNumber(item.withoutVatS20)) + parseFloat(this.convertFormattedStringToNumber(item.vatAmount20)) + parseFloat(this.convertFormattedStringToNumber(item.withoutVatS10)) + parseFloat(this.convertFormattedStringToNumber(item.vatAmount10)) + parseFloat(this.convertFormattedStringToNumber(item.amountAE20)) + parseFloat(this.convertFormattedStringToNumber(item.amountAE10)) + parseFloat(this.convertFormattedStringToNumber(item.amountE)) + parseFloat(this.convertFormattedStringToNumber(item.amountN)) + parseFloat(this.convertFormattedStringToNumber(item.amountO)) + parseFloat(this.convertFormattedStringToNumber(item.amountOE)) + parseFloat(this.convertFormattedStringToNumber(item.amountR)) + parseFloat(this.convertFormattedStringToNumber(item.amountSS)) + parseFloat(this.convertFormattedStringToNumber(item.amountZ));
            item.total = item.total.toFixed(2)
            this.calculateTotal()
        },
        calculatePrepaymentTotal() {
            let withoutVat10 = 0, withoutVat20 = 0, vatAmount10 = 0, vatAmount20 = 0, amountAE20 = 0, amountAE10 = 0, amountE = 0, amountN = 0, amountO = 0, amountOE = 0, amountR = 0, amountSS = 0, amountZ = 0, total = 0

            this.newInvoice.prepayment.forEach(prepayment => {
                withoutVat10 += parseFloat(this.convertFormattedStringToNumber(prepayment.withoutVatS10))
                withoutVat20 += parseFloat(this.convertFormattedStringToNumber(prepayment.withoutVatS20))
                vatAmount10 += parseFloat(this.convertFormattedStringToNumber(prepayment.vatAmount10))
                vatAmount20 += parseFloat(this.convertFormattedStringToNumber(prepayment.vatAmount20))
                amountAE20 += parseFloat(this.convertFormattedStringToNumber(prepayment.amountAE20))
                amountAE10 += parseFloat(this.convertFormattedStringToNumber(prepayment.amountAE10))
                amountE += parseFloat(this.convertFormattedStringToNumber(prepayment.amountE))
                amountN += parseFloat(this.convertFormattedStringToNumber(prepayment.amountN))
                amountO += parseFloat(this.convertFormattedStringToNumber(prepayment.amountO))
                amountOE += parseFloat(this.convertFormattedStringToNumber(prepayment.amountOE))
                amountR += parseFloat(this.convertFormattedStringToNumber(prepayment.amountR))
                amountSS += parseFloat(this.convertFormattedStringToNumber(prepayment.amountSS))
                amountZ += parseFloat(this.convertFormattedStringToNumber(prepayment.amountZ))
                total += parseFloat(prepayment.total)
            })

            this.prepaymentWithoutVat10 = parseFloat(withoutVat10).toFixed(2)
            this.prepaymentWithoutVat20 = parseFloat(withoutVat20).toFixed(2)
            this.prepaymentVatAmount10 = parseFloat(vatAmount10).toFixed(2)
            this.prepaymentVatAmount20 = parseFloat(vatAmount20).toFixed(2)
            this.prepaymentAmountAE20 = parseFloat(amountAE20).toFixed(2)
            this.prepaymentAmountAE10 = parseFloat(amountAE10).toFixed(2)
            this.prepaymentAmountE = parseFloat(amountE).toFixed(2)
            this.prepaymentAmountN = parseFloat(amountN).toFixed(2)
            this.prepaymentAmountO = parseFloat(amountO).toFixed(2)
            this.prepaymentAmountOE = parseFloat(amountOE).toFixed(2)
            this.prepaymentAmountR = parseFloat(amountR).toFixed(2)
            this.prepaymentAmountSS = parseFloat(amountSS).toFixed(2)
            this.prepaymentAmountZ = parseFloat(amountZ).toFixed(2)
            this.prepaymentWithoutVatTotal = (parseFloat(withoutVat10) + parseFloat(withoutVat20) + parseFloat(amountAE20) + parseFloat(amountAE10) + parseFloat(amountE) + parseFloat(amountN) + parseFloat(amountO) + parseFloat(amountOE) + parseFloat(amountR) + parseFloat(amountSS) + parseFloat(amountZ)).toFixed(2)
            this.prepaymentVatAmountTotal = (parseFloat(vatAmount10) + parseFloat(vatAmount20)).toFixed(2)
            this.prepaymentTotal = total
            //this.calculateTotal()
        },
        async invoiceData() {
            if (this.createNewInvoice != undefined) {
                await this.axios.get('SalesInvoices/GetPredefinedValues?organizationId=' + this.$store.getters.organizationId).then((response) => {
                    if (response.data && response.data.predefinedValuesId > 0) {
                        const predefined = response.data
                        const initial = JSON.parse(JSON.stringify(this.initialInvoice))
                        for (const property in initial) {
                            if (predefined[property] != null && Array.isArray(predefined[property])) {
                                const init = initial[property]
                                const prf = predefined[property]
                                for (const key in prf) {
                                    let i = init[key]
                                    let p = prf[key]
                                    if (i != undefined)
                                        for (const property in i) {
                                            if (p[property] != null && p[property] != 0) {
                                                i[property] = p[property]
                                            }
                                        }
                                    else {
                                        if (property == 'taxExemptionReasons') {
                                            initial.taxExemptionReasons.push(p)
                                        }
                                    }
                                }
                                if (property == 'items')
                                    this.predefinedItemValues = prf
                            }
                            else if (predefined[property] != null && predefined[property] != 0) {
                                initial[property] = predefined[property]
                            }
                        }
                        this.initialInvoice = JSON.parse(JSON.stringify(initial))
                        this.initialInvoice.customer = JSON.parse(JSON.stringify(this.createNewInvoice.customer))
                        this.newInvoice = JSON.parse(JSON.stringify(this.initialInvoice))
                        this.newInvoice.documentType = this.createNewInvoice.documentType
                        //this.newInvoice.customer = JSON.parse(JSON.stringify(this.createNewInvoice.customer))
                        if (this.createNewInvoice.id > 0) {
                            this.newInvoice.id = this.createNewInvoice.id;
                            this.newInvoice.invoice = this.createNewInvoice.invoice;
                        }
                        if (this.getCustomerJbkjs() != null && this.getCustomerJbkjs().length > 0) {
                            this.initialInvoice.cir = true
                            this.newInvoice.cir = true
                        }
                        else {
                            this.initialInvoice.cir = false
                            this.newInvoice.cir = false
                        }
                        return
                    }
                })
                    .catch(err => console.log(err));

                this.newInvoice.documentType = this.createNewInvoice.documentType
                this.initialInvoice.customer = JSON.parse(JSON.stringify(this.createNewInvoice.customer))
                this.newInvoice.customer = JSON.parse(JSON.stringify(this.createNewInvoice.customer))
                if (this.getCustomerJbkjs() != null && this.getCustomerJbkjs().length > 0) {
                    this.initialInvoice.cir = true
                    this.newInvoice.cir = true
                }
                else {
                    this.initialInvoice.cir = false
                    this.newInvoice.cir = false
                }
                if (this.createNewInvoice.id > 0) {
                    this.newInvoice.id = this.createNewInvoice.id;
                    this.newInvoice.invoice = this.createNewInvoice.invoice;
                }

            }
            else if (this.$route.query.id != undefined) {
                await this.axios.get('SalesInvoices/GetSalesInvoiceDetails?invoiceId=' + this.$route.query.id).then((response) => {

                    this.newInvoice = response.data
                    this.newInvoice.items.forEach(item => {
                        item.quantity = this.formatNumberToString(item.quantity)
                        item.price = this.formatNumberToString4Dig(item.price)
                        item.discount = this.formatNumberToString(item.discount)
                        item.discountAmount = this.formatNumberToString(item.discountAmount)
                        item.amountWithoutVat = this.formatNumberToString(item.amountWithoutVat)
                        item.total = this.formatNumberToString(item.total)
                    })
                    this.newInvoice.prepayment.forEach(prepayment => {
                        prepayment.withoutVatS20 = this.formatNumberToString(prepayment.withoutVatS20)
                        prepayment.withoutVatS10 = this.formatNumberToString(prepayment.withoutVatS10)
                        prepayment.vatAmount20 = this.formatNumberToString(prepayment.vatAmount20)
                        prepayment.vatAmount10 = this.formatNumberToString(prepayment.vatAmount10)
                        prepayment.amountAE20 = this.formatNumberToString(prepayment.amountAE20)
                        prepayment.amountAE10 = this.formatNumberToString(prepayment.amountAE10)
                        prepayment.amountE = this.formatNumberToString(prepayment.amountE)
                        prepayment.amountN = this.formatNumberToString(prepayment.amountN)
                        prepayment.amountO = this.formatNumberToString(prepayment.amountO)
                        prepayment.amountOE = this.formatNumberToString(prepayment.amountOE)
                        prepayment.amountR = this.formatNumberToString(prepayment.amountR)
                        prepayment.amountSS = this.formatNumberToString(prepayment.amountSS)
                        prepayment.amountZ = this.formatNumberToString(prepayment.amountZ)
                    })
                    this.newInvoice.discountVatS10 = this.formatNumberToString(this.newInvoice.discountVatS10)
                    this.newInvoice.discountVatS20 = this.formatNumberToString(this.newInvoice.discountVatS20)
                    this.newInvoice.taxExemptionReasons.forEach(reason => {
                        reason.discount = this.formatNumberToString(reason.discount)
                    })
                    this.newInvoice.exchangeRate = this.formatNumberToString4Dig(this.newInvoice.exchangeRate)
                    this.calculateTotal()
                    this.initialInvoice = JSON.parse(JSON.stringify(this.newInvoice))

                })
                    .catch(err => console.log(err));
            }
            else {
                this.$router.push({ name: 'NewInvoice', replace: true })
            }
            this.getVatDates();
            this.getBillingReferenceTypes();
            this.getTaxExemptionReasons();
            this.getTaxCategory();
            this.getUnitMeasure();
            this.getCurrencies();
            this.getItemsCodes();
            this.getItemClassification();
        },
        showTaxExemptionReasons() {
            if (this.cbVat === "withoutVat") {
                this.showExamptionReasonNumber = true
                this.showExamptionReason = true
                this.disableVat = false
            }
            else if (this.cbVat === "withVat") {
                this.showExamptionReason = false
                this.newInvoice.taxExemptionReason = {
                    key: "",
                    law: "",
                    article: "",
                    paragraph: "",
                    text: "",
                }
                this.newInvoice.taxExemptionReasonNumber = ""
                this.showExamptionReasonNumber = false
                this.disableVat = true
            }
            else {
                this.showExamptionReason = false
                this.newInvoice.taxExemptionReason = {
                    key: "",
                    law: "",
                    article: "",
                    paragraph: "",
                    text: "",
                }
                this.newInvoice.taxExemptionReasonNumber = ""
                this.showExamptionReasonNumber = false
                this.disableVat = false
            }

        },
        clearTaxExemptionReasonKey(index) {
            this.newInvoice.taxExemptionReasons[index].text = this.getDescription(this.newInvoice.taxExemptionReasons[index].category)
            this.newInvoice.taxExemptionReasons[index].key = ""
            this.newInvoice.taxExemptionReasons[index].taxExemptionReasonNumber = ""
            this.newInvoice.taxExemptionReasons[index].discount = ""
        },
        addBillingReference() {
            console.log('addBillingReference')
            if (this.billingReference && this.billingReference.id > 0) {
                const found = this.newInvoice.billingReferences.some(el => el.id === this.billingReference.id);
                if (!found)
                    this.newInvoice.billingReferences.push(this.billingReference)
                this.billingReference = null
            }
        },
        addNewBillingReference() {
            if (this.billingReference && this.billingReference.length > 0) {
                const found = this.newInvoice.billingReferences.some(el => el.invoice === this.billingReference);
                if (!found) {
                    let br = {
                        id: -1,
                        invoice: this.billingReference
                    }
                    this.newInvoice.billingReferences.push(br)
                    this.billingReference = null
                }
            }
        },
        openBillingReference(reference) {
            if (reference.id > 0) {
                let route = this.$router.resolve({ name: 'InvoiceForm', query: { id: reference.id } })
                window.open(route.href);
            }
            else {
                this.$root.displayNotificationMsg('Faktura broj ' + reference.invoice + ' nije u sistemu', 'warning')
            }
        },
        removeFromBillingReferences(index) {
            this.newInvoice.billingReferences.splice(index, 1)
        },
        clearBillingReference() {
            this.billingReferencesItems = []
        },
        changeBillingReferenceType() {
            this.newInvoice.startDate = ""
            this.newInvoice.endDate = ""
            this.newInvoice.billingReferences = []
        },
        changeVatDate() {
            this.newInvoice.contractDate = ""
        },
        addDespatchReference() {
            if (this.despatchReference && this.despatchReference.length > 0) {
                const found = this.newInvoice.despatchReferences.some(el => el === this.despatchReference);
                if (!found) {
                    let dr = {
                        despatchReference: this.despatchReference
                    }
                    this.newInvoice.despatchReferences.push(dr)
                    this.despatchReference = null
                }
            }
        },
        removeFromDespatchReferences(index) {
            this.newInvoice.despatchReferences.splice(index, 1)
        },
        clearDespatchReference() {
            this.despatchReferencesItems = []
        },
        clearPrepayment() {
            this.prepaymentItems = []
        },
        changeTaxCategory(item) {
            /*if(item.taxCategory != 'S') {
                item.vat = 0
                this.calculateLineTotal(item);
            }
            else
            {
                item.vat = ""
            }*/
            if (item.taxCategory != 'S20' && item.taxCategory != 'S10') {
                item.vat = 0
                this.calculateLineTotal(item);
            }
            else if (item.taxCategory == 'S10') {
                item.vat = 10
                this.calculateLineTotal(item);
            }
            else if (item.taxCategory == 'S20') {
                item.vat = 20
                this.calculateLineTotal(item);
            }
        },
        currencyChange() {

        },
        getNextInvoiceNumber() {
            this.axios.get('InvoiceNumber/NextInvoiceNumber?organizationId=' + this.selectedOrganization + '&documentTypeId=' + this.newInvoice.documentType.documentTypeId).then((response) => {
                this.newInvoice.invoice = response.data
            })
                .catch(err => console.log(err))
        },
        addDecimals(item) {
            if (item.quantity && item.quantity.length > 0)
                item.quantity = this.formatNumberToString(this.convertFormattedStringToNumber(item.quantity))
            if (item.price && item.price.length > 0)
                item.price = this.formatNumberToString4Dig(this.convertFormattedStringToNumber(item.price))
            if (item.discount && item.discount.length > 0)
                item.discount = this.formatNumberToString(this.convertFormattedStringToNumber(item.discount))
            if (item.discountAmount && item.discountAmount.length > 0) {
                item.discountAmount = this.formatNumberToString(this.convertFormattedStringToNumber(item.discountAmount))
            }

        },
        formatNumberToString4Dig(value) {
            value = value.toString().replace(",", ".");
            var formatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 4, maximumFractionDigits: 4 });
            return formatter.format(value);
        },
        formatNumberToString(value) {
            if (value != null)
                value = value.toString().replace(",", ".");
            var formatter = new Intl.NumberFormat("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            return formatter.format(value);
        },
        convertFormattedStringToNumber(value) {
            while (value.includes(".")) {

                value = value.replace(".", "")
            }
            value = value.replace(",", ".")

            return value
        },
        formatValue(reason) {
            if (this.newInvoice.exchangeRate && this.newInvoice.exchangeRate.length > 0)
                this.newInvoice.exchangeRate = this.formatNumberToString4Dig(this.convertFormattedStringToNumber(this.newInvoice.exchangeRate))
            if (this.newInvoice.discountVatS10 && this.newInvoice.discountVatS10.length > 0)
                this.newInvoice.discountVatS10 = this.formatNumberToString(this.convertFormattedStringToNumber(this.newInvoice.discountVatS10))
            if (this.newInvoice.discountVatS20 && this.newInvoice.discountVatS20.length > 0)
                this.newInvoice.discountVatS20 = this.formatNumberToString(this.convertFormattedStringToNumber(this.newInvoice.discountVatS20))
            if (reason && reason.discount.length > 0)
                reason.discount = this.formatNumberToString(this.convertFormattedStringToNumber(reason.discount))

        },
        formatPrepayment(prepayment) {
            if (prepayment.withoutVatS20 && prepayment.withoutVatS20.length > 0)
                prepayment.withoutVatS20 = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.withoutVatS20))
            if (prepayment.withoutVatS10 && prepayment.withoutVatS10.length > 0)
                prepayment.withoutVatS10 = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.withoutVatS10))
            if (prepayment.amountAE20 && prepayment.amountAE20.length > 0)
                prepayment.amountAE20 = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountAE20))
            if (prepayment.amountAE10 && prepayment.amountAE10.length > 0)
                prepayment.amountAE10 = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountAE10))
            if (prepayment.amountE && prepayment.amountE.length > 0)
                prepayment.amountE = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountE))
            if (prepayment.amountN && prepayment.amountN.length > 0)
                prepayment.amountN = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountN))
            if (prepayment.amountO && prepayment.amountO.length > 0)
                prepayment.amountO = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountO))
            if (prepayment.amountOE && prepayment.amountOE.length > 0)
                prepayment.amountOE = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountOE))
            if (prepayment.amountR && prepayment.amountR.length > 0)
                prepayment.amountR = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountR))
            if (prepayment.amountSS && prepayment.amountSS.length > 0)
                prepayment.amountSS = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountSS))
            if (prepayment.amountZ && prepayment.amountZ.length > 0)
                prepayment.amountZ = this.formatNumberToString(this.convertFormattedStringToNumber(prepayment.amountZ))
        },
        confirmLeave() {
            const res = this.$confirm('Do you really want to exit?', { title: 'Warning' })
            return res
        },
        confirmStayInForm() {
            return this.hasChanged && !this.confirmLeave()
        },
        beforeWindowUnload(e) {
            e.preventDefault()
            e.returnValue = ''
        },
        getBillingReferenceTypeText(item) {
            if (localStorage.getItem('language') === 'eng')
                return item.typeEng;
            else
                return item.type;
        },
        getDescriptionText(item) {
            if (localStorage.getItem('language') === 'eng')
                return item.descriptionEng;
            else
                return item.description;
        },
        getReasonKeyText(item) {
            if (localStorage.getItem('language') === 'eng')
                return item.textEng;
            else
                return item.text;
        },
        getNameText(item) {
            if (localStorage.getItem('language') === 'eng')
                return item.nameEng;
            else
                return item.name;
        },
        documentStatusText() {
            if (localStorage.getItem('language') === 'eng')
                return this.newInvoice.statusEng;
            else
                return this.newInvoice.status;
        },
    },
}
</script>
<style scoped>
.v-alert {
    position: absolute;
    margin-left: 300px;
    margin-top: 0;
}
</style>